import { submitSurveyFeedback } from '@/services/feedbackApi';
import { loggingUtility } from '@/utils/loggingUtility';
import { getOrCreateSessionId } from '@/utils/sessionUtils';
import { useCallback, useRef, useState } from 'react';

interface UseSurveyProgressProps {
  surveyId: string;
  surveyStartTime: Date | null;
  collectedMetadata: Record<string, unknown> | null;
}

export const useSurveyProgress = ({
  surveyId,
  surveyStartTime,
  collectedMetadata,
}: UseSurveyProgressProps) => {
  const [surveyResponseId, setSurveyResponseId] = useState<string | null>(null);
  const surveyResponseIdRef = useRef<string | null>(null);
  const pendingProgressRef = useRef<boolean>(false);
  const sessionIdRef = useRef<string>(getOrCreateSessionId());

  const saveProgress = useCallback(
    async (lastAnsweredQuestionId: string) => {
      if (!surveyId) {
        return;
      }

      if (pendingProgressRef.current) {
        return;
      }

      pendingProgressRef.current = true;

      try {
        const currentResponseId = surveyResponseIdRef.current;

        const response = await submitSurveyFeedback(surveyId, {
          responseId: currentResponseId || undefined,
          sessionId: sessionIdRef.current,
          response: {},
          isCompleted: false,
          startTime: surveyStartTime?.toISOString(),
          metadata: collectedMetadata || undefined,
          lastAnsweredQuestionId,
        });

        if (!currentResponseId && response.responseId) {
          surveyResponseIdRef.current = response.responseId;
          setSurveyResponseId(response.responseId);
        }
      } catch (error) {
        loggingUtility.error('Failed to save survey progress', error);
      } finally {
        pendingProgressRef.current = false;
      }
    },
    [surveyId, surveyStartTime, collectedMetadata]
  );

  return {
    surveyResponseId,
    saveProgress,
    sessionId: sessionIdRef.current,
  };
};
